/**
 * Geometry Viewer Store
 *
 * Zustand store for managing the 3D geometry viewer state.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { BuildingGeometry, ColorScheme, ViewMode, CameraPreset } from './types';
import type { IDFDocument } from '../parser/types';
import { extractGeometry } from './extractGeometry';
import { clearColorCaches } from './colorSchemes';

interface GeometryState {
  /** The extracted building geometry */
  geometry: BuildingGeometry | null;

  /** Currently selected object IDs */
  selectedIds: string[];

  /** Current view mode */
  viewMode: ViewMode;

  /** Current color scheme */
  colorScheme: ColorScheme;

  /** Whether to show surface normals */
  showNormals: boolean;

  /** Whether to show the grid */
  showGrid: boolean;

  /** Whether to show the axes helper */
  showAxes: boolean;

  /** Current camera preset (if applied) */
  currentPreset: CameraPreset | null;

  /** Whether geometry is being extracted */
  isLoading: boolean;

  /** Error message if extraction failed */
  error: string | null;

  // Actions

  /** Extract geometry from an IDF document */
  extractFromDocument: (document: IDFDocument) => void;

  /** Clear the geometry */
  clearGeometry: () => void;

  /** Set selected object IDs */
  setSelectedIds: (ids: string[]) => void;

  /** Add to selection */
  addToSelection: (id: string) => void;

  /** Remove from selection */
  removeFromSelection: (id: string) => void;

  /** Toggle selection */
  toggleSelection: (id: string) => void;

  /** Clear selection */
  clearSelection: () => void;

  /** Set view mode */
  setViewMode: (mode: ViewMode) => void;

  /** Set color scheme */
  setColorScheme: (scheme: ColorScheme) => void;

  /** Toggle show normals */
  toggleShowNormals: () => void;

  /** Toggle show grid */
  toggleShowGrid: () => void;

  /** Toggle show axes */
  toggleShowAxes: () => void;

  /** Set camera preset */
  setCameraPreset: (preset: CameraPreset) => void;

  /** Set show normals */
  setShowNormals: (show: boolean) => void;

  /** Set show grid */
  setShowGrid: (show: boolean) => void;

  /** Set show axes */
  setShowAxes: (show: boolean) => void;
}

export const useGeometryStore = create<GeometryState>()(
  devtools(
    persist(
      (set, get) => ({
        geometry: null,
        selectedIds: [],
        viewMode: 'shaded',
        colorScheme: 'zone',
        showNormals: false,
        showGrid: true,
        showAxes: true,
        currentPreset: null,
        isLoading: false,
        error: null,

      extractFromDocument: (document: IDFDocument) => {
        set({ isLoading: true, error: null });

        try {
          // Clear color caches for new document
          clearColorCaches();

          const geometry = extractGeometry(document);
          set({
            geometry,
            isLoading: false,
            selectedIds: [],
          });
        } catch (err) {
          set({
            error: err instanceof Error ? err.message : 'Failed to extract geometry',
            isLoading: false,
          });
        }
      },

      clearGeometry: () => {
        clearColorCaches();
        set({
          geometry: null,
          selectedIds: [],
          error: null,
        });
      },

      setSelectedIds: (ids: string[]) => {
        set({ selectedIds: ids });
      },

      addToSelection: (id: string) => {
        const { selectedIds } = get();
        if (!selectedIds.includes(id)) {
          set({ selectedIds: [...selectedIds, id] });
        }
      },

      removeFromSelection: (id: string) => {
        const { selectedIds } = get();
        set({ selectedIds: selectedIds.filter((selectedId) => selectedId !== id) });
      },

      toggleSelection: (id: string) => {
        const { selectedIds } = get();
        if (selectedIds.includes(id)) {
          set({ selectedIds: selectedIds.filter((selectedId) => selectedId !== id) });
        } else {
          set({ selectedIds: [...selectedIds, id] });
        }
      },

      clearSelection: () => {
        set({ selectedIds: [] });
      },

      setViewMode: (mode: ViewMode) => {
        set({ viewMode: mode });
      },

      setColorScheme: (scheme: ColorScheme) => {
        set({ colorScheme: scheme });
      },

      toggleShowNormals: () => {
        set((state) => ({ showNormals: !state.showNormals }));
      },

      toggleShowGrid: () => {
        set((state) => ({ showGrid: !state.showGrid }));
      },

      toggleShowAxes: () => {
        set((state) => ({ showAxes: !state.showAxes }));
      },

      setCameraPreset: (preset: CameraPreset) => {
        set({ currentPreset: preset });
      },

      setShowNormals: (show: boolean) => {
        set({ showNormals: show });
      },

      setShowGrid: (show: boolean) => {
        set({ showGrid: show });
      },

      setShowAxes: (show: boolean) => {
        set({ showAxes: show });
      },
    }),
    {
      name: 'geometry-viewer-storage',
      // Only persist view preferences, not runtime data (geometry, selections, loading state)
      partialize: (state) => ({
        viewMode: state.viewMode,
        colorScheme: state.colorScheme,
        showNormals: state.showNormals,
        showGrid: state.showGrid,
        showAxes: state.showAxes,
      }),
    }
    ),
    { name: 'GeometryStore' }
  )
);

/**
 * Hook to get selected surfaces from the geometry.
 */
export const useSelectedSurfaces = () => {
  const geometry = useGeometryStore((state) => state.geometry);
  const selectedIds = useGeometryStore((state) => state.selectedIds);

  if (!geometry) return [];

  return geometry.zones
    .flatMap((zone) => zone.surfaces)
    .filter((surface) => selectedIds.includes(surface.id));
};

/**
 * Hook to get zone names for the legend.
 */
export const useZoneNames = () => {
  const geometry = useGeometryStore((state) => state.geometry);

  if (!geometry) return [];

  return geometry.zones.map((zone) => zone.name);
};

/**
 * Hook to get unique construction names.
 */
export const useConstructionNames = () => {
  const geometry = useGeometryStore((state) => state.geometry);

  if (!geometry) return [];

  const constructions = new Set<string>();
  for (const zone of geometry.zones) {
    for (const surface of zone.surfaces) {
      if (surface.construction) {
        constructions.add(surface.construction);
      }
    }
  }

  return Array.from(constructions).sort();
};
